import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";

import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import StoreIcon from "@mui/icons-material/Store";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StraightenIcon from "@mui/icons-material/Straighten";

import { GC_MAP_KEY } from "../../utils/config/config";

const libraries = ["geometry", "marker"];

const mapContainerStyle = {
  width: "100%",
  height: "600px",
};

export default function BranchRouteMap({ destination }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GC_MAP_KEY,
    libraries,
  });

  const mapRef = useRef(null);

  const sourceMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  const [source, setSource] = useState(null);
  const [routePath, setRoutePath] = useState([]);

  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");

  const [error, setError] = useState("");

  // =========================================================
  // Get current location
  // =========================================================

  useEffect(() => {
    if (!navigator.geolocation) {
      setError(
        "Geolocation is not supported by this browser."
      );

      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setSource({
          lat: coords.latitude,
          lng: coords.longitude,
        });
      },

      () => {
        setError(
          "Unable to fetch current location."
        );
      },

      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
      }
    );
  }, []);

  // =========================================================
  // Create / update Advanced Markers
  // =========================================================

  useEffect(() => {
    if (!isLoaded || !mapRef.current) {
      return;
    }

    if (
      !window.google?.maps?.marker
        ?.AdvancedMarkerElement
    ) {
      console.error(
        "AdvancedMarkerElement is not available."
      );

      return;
    }

    // -------------------------------------------------------
    // Remove existing source marker
    // -------------------------------------------------------

    if (sourceMarkerRef.current) {
      sourceMarkerRef.current.map = null;
      sourceMarkerRef.current = null;
    }

    // -------------------------------------------------------
    // Remove existing destination marker
    // -------------------------------------------------------

    if (destinationMarkerRef.current) {
      destinationMarkerRef.current.map = null;
      destinationMarkerRef.current = null;
    }

    // -------------------------------------------------------
    // Source marker
    // -------------------------------------------------------

    if (source) {
      sourceMarkerRef.current =
        new window.google.maps.marker.AdvancedMarkerElement(
          {
            map: mapRef.current,

            position: source,

            title: "Current Location",
          }
        );
    }

    // -------------------------------------------------------
    // Destination marker
    // -------------------------------------------------------

    if (destination) {
      const destinationPosition = {
        lat: Number(destination.latitude),
        lng: Number(destination.longitude),
      };

      destinationMarkerRef.current =
        new window.google.maps.marker.AdvancedMarkerElement(
          {
            map: mapRef.current,

            position: destinationPosition,

            title: "Branch",
          }
        );
    }

    // -------------------------------------------------------
    // Cleanup
    // -------------------------------------------------------

    return () => {
      if (sourceMarkerRef.current) {
        sourceMarkerRef.current.map = null;
        sourceMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
        destinationMarkerRef.current = null;
      }
    };
  }, [
    isLoaded,
    source,
    destination,
  ]);

  // =========================================================
  // Calculate route using Routes API
  // =========================================================

  useEffect(() => {
    if (
      !isLoaded ||
      !source ||
      !destination
    ) {
      return;
    }

    const destinationPosition = {
      lat: Number(destination.latitude),
      lng: Number(destination.longitude),
    };

    // -------------------------------------------------------
    // Validate destination
    // -------------------------------------------------------

    if (
      !Number.isFinite(
        destinationPosition.lat
      ) ||
      !Number.isFinite(
        destinationPosition.lng
      )
    ) {
      setError(
        "Invalid destination coordinates."
      );

      return;
    }

    const calculateRoute = async () => {
      try {
        setError("");

        setRoutePath([]);
        setDistance("");
        setDuration("");

        // ---------------------------------------------------
        // Routes API
        // ---------------------------------------------------

        const response = await fetch(
          "https://routes.googleapis.com/directions/v2:computeRoutes",
          {
            method: "POST",

            headers: {
              "Content-Type": "application/json",

              "X-Goog-Api-Key": GC_MAP_KEY,

              "X-Goog-FieldMask":
                "routes.distanceMeters,routes.duration,routes.polyline.encodedPolyline",
            },

            body: JSON.stringify({
              origin: {
                location: {
                  latLng: {
                    latitude: source.lat,
                    longitude: source.lng,
                  },
                },
              },

              destination: {
                location: {
                  latLng: {
                    latitude:
                      destinationPosition.lat,

                    longitude:
                      destinationPosition.lng,
                  },
                },
              },

              travelMode: "DRIVE",

              routingPreference:
                "TRAFFIC_AWARE",

              computeAlternativeRoutes: false,
            }),
          }
        );

        // ---------------------------------------------------
        // API Error
        // ---------------------------------------------------

        if (!response.ok) {
          const errorBody =
            await response.text();

          console.error(
            "Routes API error:",
            errorBody
          );

          throw new Error(
            "Unable to calculate route."
          );
        }

        const data =
          await response.json();

        const route =
          data?.routes?.[0];

        if (!route) {
          throw new Error(
            "No route found between the locations."
          );
        }

        // ===================================================
        // Distance
        // ===================================================

        const distanceMeters =
          route.distanceMeters || 0;

        const distanceKm =
          distanceMeters / 1000;

        if (distanceMeters < 1000) {
          setDistance(
            `${Math.round(
              distanceMeters
            )} m`
          );
        } else {
          setDistance(
            `${distanceKm.toFixed(1)} km`
          );
        }

        // ===================================================
        // Duration
        // ===================================================

        const durationSeconds =
          parseInt(
            route.duration?.replace(
              "s",
              ""
            ) || "0",
            10
          );

        const durationMinutes =
          Math.ceil(
            durationSeconds / 60
          );

        if (durationMinutes < 60) {
          setDuration(
            `${durationMinutes} min`
          );
        } else {
          const hours =
            Math.floor(
              durationMinutes / 60
            );

          const minutes =
            durationMinutes % 60;

          if (minutes > 0) {
            setDuration(
              `${hours} hr ${minutes} min`
            );
          } else {
            setDuration(
              `${hours} hr`
            );
          }
        }

        // ===================================================
        // Decode route polyline
        // ===================================================

        const encodedPolyline =
          route.polyline
            ?.encodedPolyline;

        if (!encodedPolyline) {
          console.warn(
            "Routes API did not return a polyline."
          );

          return;
        }

        // Make sure geometry library exists
        if (
          !window.google?.maps?.geometry
            ?.encoding
        ) {
          throw new Error(
            "Google Maps geometry library is not loaded."
          );
        }

        const decodedPath =
          window.google.maps.geometry.encoding.decodePath(
            encodedPolyline
          );

        const path =
          decodedPath.map(
            (point) => ({
              lat: point.lat(),
              lng: point.lng(),
            })
          );

        setRoutePath(path);

        // ===================================================
        // Fit map to route
        // ===================================================

        if (
          mapRef.current &&
          path.length > 0
        ) {
          const bounds =
            new window.google.maps.LatLngBounds();

          path.forEach((point) => {
            bounds.extend(point);
          });

          mapRef.current.fitBounds(
            bounds,
            80
          );
        }
      } catch (err) {
        console.error(
          "Route calculation failed:",
          err
        );

        setError(
          err?.message ||
            "Unable to calculate route."
        );
      }
    };

    calculateRoute();
  }, [
    isLoaded,
    source,
    destination,
  ]);

  // =========================================================
  // Loading
  // =========================================================

  if (loadError) {
    return (
      <Alert severity="error">
        Failed to load Google Maps.
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        textAlign="center"
        mt={5}
      >
        <CircularProgress />
      </Box>
    );
  }

  // =========================================================
  // Error
  // =========================================================

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  // =========================================================
  // Destination validation
  // =========================================================

  if (!destination) {
    return (
      <Alert severity="info">
        Destination is not available.
      </Alert>
    );
  }

  const destinationPosition = {
    lat: Number(
      destination.latitude
    ),

    lng: Number(
      destination.longitude
    ),
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {/* =====================================================
          Floating Route Summary
      ====================================================== */}

      <Card
        sx={{
          position: "absolute",

          top: 16,
          left: 16,

          zIndex: 10,

          width: 320,

          borderRadius: 3,

          boxShadow: 6,
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            fontWeight={700}
          >
            Route Summary
          </Typography>

          <Divider
            sx={{
              my: 2,
            }}
          />

          {/* Current Location */}

          <Box
            display="flex"
            alignItems="center"
            mb={1}
          >
            <MyLocationIcon
              color="primary"
            />

            <Typography ml={1}>
              Current Location
            </Typography>
          </Box>

          {/* Branch */}

          <Box
            display="flex"
            alignItems="center"
          >
            <StoreIcon
              color="error"
            />

            <Typography ml={1}>
              Branch
            </Typography>
          </Box>

          <Divider
            sx={{
              my: 2,
            }}
          />

          {/* Distance + Duration */}

          <Box
            display="flex"
            justifyContent="space-between"
            gap={1}
          >
            <Chip
              icon={
                <StraightenIcon />
              }
              color="primary"
              label={
                distance || "--"
              }
            />

            <Chip
              icon={
                <AccessTimeIcon />
              }
              color="success"
              label={
                duration || "--"
              }
            />
          </Box>
        </CardContent>
      </Card>

      {/* =====================================================
          Google Map
      ====================================================== */}

      <GoogleMap
        mapContainerStyle={
          mapContainerStyle
        }

        onLoad={(map) => {
          mapRef.current = map;
        }}

        options={{
          // Required for AdvancedMarkerElement
          mapId: "YOUR_MAP_ID",

          streetViewControl: false,

          mapTypeControl: false,

          fullscreenControl: true,
        }}
      >
        {/* =================================================
            Route
        ================================================== */}

        {routePath.length > 0 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: "#1976d2",

              strokeWeight: 6,

              strokeOpacity: 0.9,

              clickable: false,

              geodesic: true,
            }}
          />
        )}
      </GoogleMap>
    </Box>
  );
}